import React from 'react';
import { useTranslation } from 'react-i18next';
import ProfileImage from '../Header/ProfileImage';
import Badge from '../shared/TailwindComponents/Badge';
import { normalise } from '../shared/access/accessModel';

interface LawyerRecommendation {
  lawyer_id: string;
  first_name: string;
  last_name: string;
  specialty: string[];
  experience: number;
  hourly_rate: number;
  location: string[] | string;
  ratings_average: number;
  reason: string;
  profilePicture?: string;
}

interface RecommendedLawyerItemProps {
  lawyer: LawyerRecommendation;
  onClick: () => void;
}

const RecommendedLawyerItem: React.FC<RecommendedLawyerItemProps> = ({ lawyer, onClick }) => {
  const { t } = useTranslation();
  const fullName = `${lawyer.first_name} ${lawyer.last_name}`.trim();
  const initials = `${lawyer.first_name?.[0] || ''}${lawyer.last_name?.[0] || ''}`.toUpperCase();

  const locations = Array.isArray(lawyer.location)
    ? lawyer.location
    : lawyer.location === 'Not specified' || !lawyer.location
    ? ['Not specified']
    : [lawyer.location];

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<span key={i} className="text-warning-400">★</span>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(
          <span key={i} className="text-warning-400 relative">
            ★
            <span className="absolute start-0 text-gray-300">☆</span>
          </span>
        );
      } else {
        stars.push(<span key={i} className="text-gray-300">☆</span>);
      }
    }
    return stars;
  };

  return (
    <div
      onClick={onClick}
      className="glass rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 flex flex-col gap-4"
    >
      <div className="flex items-center gap-4">
        <ProfileImage
          profilePicture={normalise(lawyer).avatar}
          email={''}
          initials={initials}
          name={fullName}
        />
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{fullName}</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {lawyer.specialty.map((spec, index) => (
              <Badge key={index} color="indigo" className="text-xs font-medium">
                {spec}
              </Badge>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
        <div>
          <span className="font-medium">{t('Experience')}: </span>
          {lawyer.experience ? `${lawyer.experience} years` : t('Not specified')}
        </div>
        <div>
          <span className="font-medium">{t('Rate')}: </span>
          {lawyer.hourly_rate ? `$${lawyer.hourly_rate}/hour` : t('Not specified')}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300">{t('Locations')}: </span>
        {locations.map((loc, index) => (
          <Badge key={index} color="gray" className="text-xs">
            {loc}
          </Badge>
        ))}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300">{t('Rating')}: </span>
        {lawyer.ratings_average ? (
          <div className="flex items-center gap-1">
            {renderStars(lawyer.ratings_average)}
            <span className="text-gray-500 dark:text-gray-400">({lawyer.ratings_average.toFixed(1)})</span>
          </div>
        ) : (
          t('No ratings')
        )}
      </div>
      <div className="mt-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{t('Why Recommended')}:</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 italic">{lawyer.reason}</p>
      </div>
    </div>
  );
};

export default RecommendedLawyerItem;